import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clientVisitService } from "../../api/clientVisitService";
import ApprovalProgress from "../../components/ui/ApprovalProgress";
import Badge from "../../components/ui/Badge";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Tabs from "../../components/ui/Tabs";
import { useToast } from "../../components/ui/useToast";
import { getErrorMessage } from "../../utils/apiError";
import { formatDate } from "../../utils/date";
import { cx } from "../../utils/classNames";
import {
  CLIENT_VISIT_KEY,
  STATUS_TABS,
  visitStatusOf,
} from "./clientVisitUtils";
import FileClientVisitModal from "./FileClientVisitModal";
import useClientVisitOptions from "./useClientVisitOptions";
import ui from "../../components/ui/ui.module.css";
import styles from "./ClientVisits.module.css";

const COLUMN_COUNT = 5;
const EMPTY = [];

/** My view: file a visit to clients ahead of the day, and follow its approval. */
const MyClientVisitsView = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  const options = useClientVisitOptions();
  const canFile = !!options.data?.canFile;
  const query = useQuery({
    queryKey: [CLIENT_VISIT_KEY, "my", status],
    queryFn: () => clientVisitService.getMine({ status }),
    enabled: canFile,
  });
  const rows = Array.isArray(query.data) ? query.data : EMPTY;

  const cancelMutation = useMutation({
    mutationFn: (id) => clientVisitService.cancel(id),
    onSuccess: () => toast.success("Client visit cancelled."),
    onError: (error) => {
      // A 403 is already toasted by apiClient
      if (error?.response?.status !== 403)
        toast.error(getErrorMessage(error, "Unable to cancel the request."));
    },
    onSettled: () => {
      setCancelTarget(null);
      queryClient.invalidateQueries({ queryKey: [CLIENT_VISIT_KEY] });
    },
  });

  if (options.isLoading) return <div className={ui.muted}>Loading…</div>;
  if (!canFile) {
    return (
      <div className={cx(ui.alert, ui.alertGray)}>
        {options.isError
          ? getErrorMessage(options.error, "Unable to load client visits.")
          : "Your role is not set up to file client visits."}
      </div>
    );
  }

  const message = query.isLoading
    ? "Loading your client visits…"
    : query.isError
      ? getErrorMessage(query.error, "Unable to load your client visits.")
      : rows.length === 0
        ? "No client visits."
        : "";

  return (
    <div className={ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <h4 className="page-title">Client Visits</h4>
          <p className="page-subtitle">
            File a visit before the day. Once it is approved, you can check in
            at the client that day.
          </p>
        </div>
        <button
          type="button"
          className={cx(ui.button, ui.primary)}
          onClick={() => setIsFormOpen(true)}
        >
          <i className="bi bi-plus-lg"></i>
          File client visit
        </button>
      </div>

      <div className={ui.card}>
        <Tabs tabs={STATUS_TABS} active={status} onChange={setStatus} />
        <div className={ui.tableWrapper}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Clients</th>
                <th>Purpose</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {message && (
                <tr>
                  <td
                    colSpan={COLUMN_COUNT}
                    className={
                      query.isError
                        ? "text-danger"
                        : cx(ui.muted, "text-center")
                    }
                  >
                    {message}
                  </td>
                </tr>
              )}
              {rows.map((visit) => {
                const meta = visitStatusOf(visit);
                return (
                  <tr key={visit.id}>
                    <td className={cx(ui.noWrap, ui.strong)}>
                      {formatDate(visit.date)}
                    </td>
                    <td>
                      {(visit.clients ?? []).map((c) => (
                        <div key={c.id}>
                          <span className={styles.clientName}>{c.name}</span>
                          {!c.hasLocation && (
                            <span className={styles.noLocation}>
                              No saved location: check-ins are flagged for
                              review
                            </span>
                          )}
                        </div>
                      ))}
                    </td>
                    <td className={ui.truncate} title={visit.purpose}>
                      {visit.purpose || "—"}
                      {visit.reviewRemarks && (
                        <div className={ui.muted}>“{visit.reviewRemarks}”</div>
                      )}
                    </td>
                    <td>
                      <Badge tone={meta.tone}>{meta.label}</Badge>
                      <ApprovalProgress
                        isPending={visit.status === "PENDING" && !visit.expired}
                        approvals={visit.approvals ?? []}
                        waitingFor={visit.waitingFor}
                        managersAbove={visit.managersAbove}
                      />
                    </td>
                    <td className="text-end">
                      {visit.canCancel && (
                        <button
                          type="button"
                          className={cx(
                            ui.button,
                            ui.dangerOutline,
                            ui.buttonSmall,
                          )}
                          onClick={() => setCancelTarget(visit)}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {isFormOpen && (
        <FileClientVisitModal
          clients={options.data?.clients ?? EMPTY}
          onClose={() => setIsFormOpen(false)}
        />
      )}

      {cancelTarget && (
        <ConfirmDialog
          title="Cancel client visit?"
          message={`Cancel your client visit on ${formatDate(cancelTarget.date)}?`}
          confirmLabel="Cancel visit"
          tone="danger"
          isPending={cancelMutation.isPending}
          onConfirm={() => cancelMutation.mutate(cancelTarget.id)}
          onClose={() => setCancelTarget(null)}
        />
      )}
    </div>
  );
};

export default MyClientVisitsView;
